import React, { useCallback, useEffect, useRef, useState } from "react";
import FourLinkConfigTool from "./features/fourlink-config"; // Importa tu componente reestructurado
import GoogleTranslate from "./GoogleTranslate";
import InstanceSelector from "./InstanceSelector";
import LoginScreen from "./LoginScreen";
import CreateUserScreen from "./CreateUserScreen";
import { exportLocalStorageToCsv, importSessionFile } from "./storageCsv";
import * as mockApi from "./mockApi";
import { clearAuthToken, getAuthToken } from "./api";

const STORAGE_KEY = 'fourlink-config-tool-state-v1';
const SELECTED_INSTANCE_STORAGE_KEY = 'fourlink-selected-instance-v1';
const normalizeInstanceValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
};

const normalizeInstancePayload = (payload) => {
  if (payload === undefined || payload === null) {
    return null;
  }

  if (typeof payload === 'string' || typeof payload === 'number') {
    const id = normalizeInstanceValue(payload);
    if (!id) {
      return null;
    }
    return {
      id,
      name: `Instancia ${id}`,
      description: null,
    };
  }

  if (typeof payload !== 'object') {
    return null;
  }

  const id = normalizeInstanceValue(
    payload.id ??
    payload.ID ??
    payload.instancia_id ??
    payload.id_instancia ??
    payload.fk_instancia ??
    payload.pk_instancia ??
    payload.codigo ??
    payload.code
  );

  if (!id) {
    return null;
  }

  const name = normalizeInstanceValue(
    payload.name ??
    payload.nombre ??
    payload.descripcion ??
    payload.titulo ??
    payload.label ??
    ''
  );

  const description = normalizeInstanceValue(
    payload.description ??
    payload.detalle ??
    payload.descripcion_larga ??
    payload.descripcion ??
    ''
  );

  return {
    id,
    name: name || `Instancia ${id}`,
    description: description || null,
  };
};

const readPersistedInstance = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SELECTED_INSTANCE_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return normalizeInstancePayload(parsed);
  } catch (_) {
    return null;
  }
};

const persistSelectedInstance = (instance) => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  if (!instance) {
    window.localStorage.removeItem(SELECTED_INSTANCE_STORAGE_KEY);
    return;
  }
  const normalized = normalizeInstancePayload(instance);
  if (!normalized) {
    window.localStorage.removeItem(SELECTED_INSTANCE_STORAGE_KEY);
    return;
  }
  const payload = {
    id: normalized.id,
    name: normalized.name,
    description: normalized.description || null,
  };
  try {
    window.localStorage.setItem(SELECTED_INSTANCE_STORAGE_KEY, JSON.stringify(payload));
  } catch (_) {
    // ignore write errors
  }
};
function App() {
  const initialToken = getAuthToken();
  const VIEW_LOGIN = 'login';
  const VIEW_LANDING = 'landing';
  const VIEW_TOOL = 'tool';
  const VIEW_TOOL_DEMO = 'tool-demo';
  const VIEW_INSTANCE_SELECTION = 'instance-selection';
  const VIEW_CREATE_USER = 'create-user';

  const [view, setView] = useState(initialToken ? VIEW_LANDING : VIEW_LOGIN);
  const [hasSavedSession, setHasSavedSession] = useState(false);
  const [instanceKey, setInstanceKey] = useState(0); // Para forzar remount limpio si hace falta
  const [selectedInstance, setSelectedInstance] = useState(() => readPersistedInstance());
  const [authToken, setAuthTokenState] = useState(initialToken);
  const [authUser, setAuthUser] = useState(null);
  const fileInputRef = useRef(null);
  const translatorWidget = (
    <GoogleTranslate
      includedLanguages="es,en,ca"
      pageLanguage="es"
      wrapperClassName="static bg-gray-50"
    />
  );

  const refreshHasSavedSession = useCallback(() => {
    try {
      const raw = typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem(STORAGE_KEY)
        : null;
      setHasSavedSession(!!raw);
    } catch (_) {
      setHasSavedSession(false);
    }
  }, []);

  useEffect(() => {
    refreshHasSavedSession();
  }, [refreshHasSavedSession]);

  useEffect(() => {
    persistSelectedInstance(selectedInstance);
  }, [selectedInstance]);

  const resetWorkspaceState = useCallback(() => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(STORAGE_KEY);
        window.localStorage.removeItem(SELECTED_INSTANCE_STORAGE_KEY);
      }
    } catch (_) {}
    setHasSavedSession(false);
    setSelectedInstance(null);
    setInstanceKey(k => k + 1);
  }, []);

  const startNewSession = () => {
    resetWorkspaceState();
    setView(VIEW_INSTANCE_SELECTION);
  };

  const continueSession = () => {
    const persisted = selectedInstance || readPersistedInstance();
    if (persisted) {
      setSelectedInstance(persisted);
      setInstanceKey(k => k + 1); // fuerza remount para rehidratar todo el estado guardado
      setView(VIEW_TOOL);
      return;
    }
    setView(VIEW_INSTANCE_SELECTION);
  };

  const startDemo = () => {
    setSelectedInstance({
      id: 'demo-local',
      name: 'Demo offline',
      description: 'Datos de ejemplo locales',
    });
    setHasSavedSession(false);
    setInstanceKey(k => k + 1);
    setView(VIEW_TOOL_DEMO);
  };

  const handleLoginSuccess = ({ token, user }) => {
    setAuthTokenState(token);
    setAuthUser(user || null);
    refreshHasSavedSession();
    setView(VIEW_LANDING);
  };

  const handleLogout = () => {
    clearAuthToken();
    setAuthTokenState('');
    setAuthUser(null);
    resetWorkspaceState();
    setView(VIEW_LOGIN);
  };

  const exportSession = () => {
    exportLocalStorageToCsv(STORAGE_KEY);
  };

  const onImportClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const onImportChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      const sessionData = await importSessionFile(file, STORAGE_KEY);
      setHasSavedSession(true);
    setInstanceKey(k => k + 1); // por si acaso
    const importedInstance = normalizeInstancePayload(sessionData?.selectedInstance);
    if (importedInstance) {
      setSelectedInstance(importedInstance);
    }
      setView(VIEW_TOOL); // ir directamente a la herramienta
    } catch (err) {
      alert('Error importando el CSV: ' + (err?.message || String(err)));
    } finally {
      e.target.value = '';
    }
  };

  const handleInstanceSelected = (instance) => {
    const normalized = normalizeInstancePayload(instance);
    if (!normalized) {
      return;
    }
    setSelectedInstance(normalized);
    setHasSavedSession(false);
    setInstanceKey(k => k + 1);
    setView(VIEW_TOOL);
  };

  const canManageUsers = authUser && (authUser.role === 'admin' || authUser.role === 'Administrador' || authUser.role === 'administrador');


  if (view === VIEW_LOGIN) {
    return (
      <>
        <div className="static bg-gray-50">
          {translatorWidget}
        </div>
        <LoginScreen onSuccess={handleLoginSuccess} />
      </>
    );
  }

  if (view === VIEW_LANDING) {
    return (
      <><div className="static bg-gray-50">
        {translatorWidget}
      </div><div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
          <div className="w-full max-w-xl bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="mb-6 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{'Herramienta de Configuraci\u00F3n 4Link'}</h1>
                  <p className="text-gray-600 mt-1">{'Elige c\u00F3mo quieres comenzar'}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="text-sm font-semibold text-blue-700 hover:text-blue-800"
                >
                  {'Cerrar sesi\u00F3n'}
                </button>
              </div>
            </div>

              <div className="space-y-4">
              {canManageUsers && (
                <button
                  onClick={() => setView(VIEW_CREATE_USER)}
                  className="w-full px-6 py-4 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                >
                  {'Crear usuarios'}
                </button>
              )}

              <button
                onClick={startNewSession}
                className="w-full px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                {'Nueva sesi\u00F3n'}
              </button>

              <button
                onClick={continueSession}
                disabled={!hasSavedSession}
                className={`w-full px-6 py-4 rounded-lg font-semibold transition-colors ${hasSavedSession ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
              >
                {'Continuar sesi\u00F3n anterior'}
              </button>

              <button
                onClick={startDemo}
                className="w-full px-6 py-4 rounded-lg font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
              >
                {'Probar con datos de ejemplo'}
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <button
                  onClick={exportSession}
                  disabled={!hasSavedSession}
                  className={`w-full px-4 py-3 rounded-lg text-sm font-semibold transition-colors ${hasSavedSession ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                >
                  {'Exportar sesi\u00F3n (CSV)'}
                </button>

                <div>
                  <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv,.xlsx,.xls"
                  onChange={onImportChange}
                  className="hidden" />
                  <button
                    onClick={onImportClick}
                    className="w-full px-4 py-3 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 transition-colors"
                  >
                    Importar desde CSV
                  </button>
                </div>
              </div>

              {!hasSavedSession && (
                <p className="text-xs text-gray-500 text-center">{'No se encontr\u00F3 una sesi\u00F3n guardada'}</p>
              )}
            </div>
          </div>
        </div></>
    );
  }

  if (view === VIEW_INSTANCE_SELECTION) {
    return (
      <>
        <div className="static bg-gray-50">
          {translatorWidget}
        </div>
        <InstanceSelector
          onSelect={handleInstanceSelected}
          onCancel={() => setView(VIEW_LANDING)}
          initialSelection={selectedInstance}
        />
      </>
    );
  }

  const handleExit = () => {
    refreshHasSavedSession();
    setView(VIEW_LANDING);
  };

  if (view === VIEW_TOOL) {
    return (
      <div>
        {translatorWidget}
        <FourLinkConfigTool key={instanceKey} onExit={handleExit} selectedInstance={selectedInstance} />
      </div>
    );
  }

  if (view === VIEW_TOOL_DEMO) {
    return (
      <div>
        {translatorWidget}
        <FourLinkConfigTool
          key={`demo-${instanceKey}`}
          onExit={handleExit}
          selectedInstance={selectedInstance}
          apiOverrides={mockApi}
        />
      </div>
    );
  }

  if (view === VIEW_CREATE_USER) {
    return (
      <div>
        {translatorWidget}
        <CreateUserScreen
          currentUser={authUser}
          onExit={() => setView(VIEW_LANDING)}
        />
      </div>
    );
  }

  return null;
}

export default App;
